import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Route, MapPin, Clock, Gauge, ExternalLink, Volume2, VolumeX, Tag } from "lucide-react";

const MOTRI_AVATAR = "https://static.prod-images.emergentagent.com/jobs/d4fbb020-9bf0-44b5-b881-86e2b1f2c47e/images/d1e3e274fc17c48182a53bee2ff2418d7c33e0a38e9a829cb22118d5173b10b4.png";

const DIFF_COLORS = { green: "text-green-400", blue: "text-blue-400", red: "text-orange-400", black: "text-red-400" };
const DIFF_KEYS = { green: "difficulty_easy", blue: "difficulty_moderate", red: "difficulty_hard", black: "difficulty_extreme" };

let _audioCtx = null;
export function playDing() {
  try {
    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.25);
  } catch (_) { /* silent fail */ }
}

function balanceMarkdown(text) {
  let result = text;
  const boldCount = (result.match(/\*\*/g) || []).length;
  if (boldCount % 2 !== 0) result += '**';
  const singleCount = (result.replace(/\*\*/g, '').match(/\*/g) || []).length;
  if (singleCount % 2 !== 0) result += '*';
  const btCount = (result.match(/`/g) || []).length;
  if (btCount % 2 !== 0) result += '`';
  return result;
}

function TypingText({ text, onComplete, onScroll, tripsMap, t }) {
  const [displayed, setDisplayed] = useState("");
  const idx = useRef(0);

  useEffect(() => {
    idx.current = 0;
    setDisplayed("");
    const speed = Math.max(8, Math.min(25, 2000 / (text.length || 1)));
    const timer = setInterval(() => {
      idx.current += 1;
      const chunk = text.slice(0, idx.current);
      setDisplayed(chunk);
      onScroll?.();
      if (idx.current >= text.length) {
        clearInterval(timer);
        onComplete?.();
      }
    }, speed);
    return () => clearInterval(timer);
  }, [text, onComplete, onScroll]);

  return <MarkdownContent content={balanceMarkdown(displayed)} tripsMap={tripsMap} t={t} />;
}

function TripCard({ href, children, tripsMap, t }) {
  const tripId = href?.replace("/passeio/", "");
  const trip = tripsMap?.[tripId];

  if (!trip) {
    return (
      <Link
        to={href}
        className="inline-flex items-center gap-1.5 px-3 py-1 my-0.5 rounded-lg bg-primary/10 border border-primary/25 text-primary hover:bg-primary/20 hover:border-primary/40 transition-colors font-medium text-sm no-underline"
      >
        <Route className="h-3.5 w-3.5 shrink-0 opacity-70" />
        <span>{children}</span>
      </Link>
    );
  }

  return (
    <Link to={href} className="block my-3 no-underline group" data-testid={`trip-card-${tripId}`}>
      <div className="flex overflow-hidden rounded-xl border border-border/60 bg-background/60 backdrop-blur-sm hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all duration-200">
        {trip.main_photo && (
          <div className="w-28 sm:w-36 shrink-0 overflow-hidden">
            <img src={trip.main_photo} alt={trip.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          </div>
        )}
        <div className="flex-1 p-3 sm:p-4 min-w-0">
          <h4 className="font-semibold text-sm sm:text-base text-foreground group-hover:text-primary transition-colors truncate">
            {trip.title}
          </h4>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-muted-foreground">
            {trip.region && (
              <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{trip.region}{trip.distrito ? `, ${trip.distrito}` : ""}</span>
            )}
            {trip.distance_km > 0 && (
              <span className="flex items-center gap-1"><Route className="h-3 w-3" />{trip.distance_km} km</span>
            )}
            {trip.duration_hours > 0 && (
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{trip.duration_hours}h</span>
            )}
            {trip.difficulty && (
              <span className={`flex items-center gap-1 ${DIFF_COLORS[trip.difficulty] || ""}`}>
                <Gauge className="h-3 w-3" />{t(DIFF_KEYS[trip.difficulty] || trip.difficulty)}
              </span>
            )}
          </div>
          {trip.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {trip.tags.map(tag => (
                <span key={tag} className="px-1.5 py-0.5 rounded text-[10px] bg-muted/50 text-muted-foreground">{tag}</span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center pr-3 opacity-0 group-hover:opacity-60 transition-opacity">
          <ExternalLink className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    </Link>
  );
}

function MarkdownContent({ content, tripsMap, t }) {
  return (
    <ReactMarkdown
      components={{
        a: ({ href, children }) => {
          if (href?.startsWith("/passeio/")) {
            return <TripCard href={href} tripsMap={tripsMap} t={t}>{children}</TripCard>;
          }
          if (href?.startsWith("/")) {
            return <Link to={href} className="text-primary underline hover:text-primary/80 font-medium">{children}</Link>;
          }
          return <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80">{children}</a>;
        },
        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
        strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
        ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-1">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 space-y-1">{children}</ol>,
        li: ({ children }) => <li>{children}</li>,
        h3: ({ children }) => <h3 className="font-semibold mt-3 mb-1">{children}</h3>,
        h4: ({ children }) => <h4 className="font-semibold mt-2 mb-1">{children}</h4>,
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

export function ChatMessage({ message, isTyping, onTypingComplete, onScroll, tripsMap, t, canVoice, onPlayTTS, onStopTTS, playingTTS, msgIdx }) {
  const isUser = message.role === "user";
  const isPlaying = playingTTS === msgIdx;

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-4`} data-testid={`chat-message-${message.role}`}>
      {!isUser && (
        <img src={MOTRI_AVATAR} alt="Motri" className="w-8 h-8 rounded-full object-cover mr-2.5 mt-0.5 shrink-0" />
      )}
      <div
        className={`max-w-[90%] sm:max-w-[80%] rounded-2xl px-4 py-3 text-xs leading-relaxed ${
          isUser
            ? "bg-primary text-primary-foreground rounded-br-md"
            : "bg-card/90 border border-border text-card-foreground rounded-bl-md backdrop-blur-sm"
        }`}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap">{message.text}</p>
        ) : isTyping ? (
          <TypingText text={message.text} onComplete={onTypingComplete} onScroll={onScroll} tripsMap={tripsMap} t={t} />
        ) : (
          <MarkdownContent content={message.text} tripsMap={tripsMap} t={t} />
        )}
        {!isUser && canVoice && !isTyping && message.text && (
          <button
            onClick={() => isPlaying ? onStopTTS() : onPlayTTS(message.text, msgIdx)}
            disabled={playingTTS !== null && !isPlaying}
            className={`mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
              isPlaying
                ? "bg-primary/15 text-primary border border-primary/30 shadow-sm shadow-primary/10 hover:bg-destructive/15 hover:text-destructive hover:border-destructive/30"
                : "bg-muted/50 text-muted-foreground border border-border/50 hover:bg-primary/10 hover:text-primary hover:border-primary/30"
            } disabled:opacity-30`}
            data-testid={`tts-btn-${msgIdx}`}
          >
            {isPlaying ? (
              <><VolumeX className="h-3.5 w-3.5 animate-pulse" />{t("tts_stop")}</>
            ) : (
              <><Volume2 className="h-3.5 w-3.5" />{t("tts_listen")}</>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export { MOTRI_AVATAR };
